import { useCallback, useState } from 'react';
import { dealerProvider } from '../../services/api';
import {
  IGroupPermissionsState,
  IProfileGroup,
  IPermissionProfiles,
  IPaginationGroupProfiles,
} from '../../interfaces';
import { sendNotification } from '../../utils/functions';
import { IResponsibleChannel } from '../../interfaces';
import { toast } from 'react-toastify';

const toastConfig = {
  autoClose: 4000,
  hideProgressBar: true,
  type: 'success',
};

export const usePermissions = (): [
  IGroupPermissionsState,
  ({
    active,
    currentPage,
    pageSize,
  }: {
    active?: boolean;
    currentPage?: number;
    pageSize?: number;
  }) => void,
  () => void,
  (updatedData: IProfileGroup | any, update?: boolean) => any,
  (id: number, active: boolean) => any,
  (id: number | string) => Promise<void>,
  () => void,
  (email: string) => any,
] => {
  const [groupState, setGroupState] = useState<IGroupPermissionsState>({
    isLoading: false,
    error: null,
    modalLoading: false,
    economicGroups: [],
    profileGroups: [],
    permissionsProfile: [],
    profilePagination: {},
    responsibleChannel: [],
  });

  const fetchData = useCallback(
    async (endpoint: string, onSuccess: (data: any) => void) => {
      try {
        setGroupState(prevState => ({ ...prevState, isLoading: true }));
        const response = await dealerProvider.get(endpoint, {});

        onSuccess(response.data.data);

        setGroupState(prevState => ({ ...prevState, error: null }));
      } catch (err) {
        setGroupState(prevState => ({ ...prevState, error: err as null }));
      } finally {
        setGroupState(prevState => ({ ...prevState, isLoading: false }));
      }
    },
    [],
  );

  const postData = useCallback(async (endpoint: string, data: any) => {
    try {
      setGroupState(prevState => ({ ...prevState, modalLoading: true }));
      const response = await dealerProvider.post(endpoint, data, {});

      setGroupState(prevState => ({ ...prevState, error: null }));
      return response.data.data;
    } catch (err: any) {
      setGroupState(prevState => ({
        ...prevState,
        error: err as any,
        modalLoading: false,
      }));
      throw err;
    } finally {
      setGroupState(prevState => ({ ...prevState, modalLoading: false }));
    }
  }, []);

  const fetchResponsibleChannel = useCallback(() => {
    fetchData(
      'api/CanaisResponsaveis/listar',
      (data: { canalResponsavelDTO?: IResponsibleChannel[] }) => {
        const channels = Array.isArray(data?.canalResponsavelDTO)
          ? data.canalResponsavelDTO
          : [];
        const formattedChannel = channels.map((area: IResponsibleChannel) => ({
          value: area.id,
          label: area.nome,
        }));
        setGroupState(prevState => ({
          ...prevState,
          responsibleChannel: formattedChannel,
        }));
      },
    );
  }, [fetchData]);
  const fetchProfilesGroups = useCallback(
    ({
      active,
      currentPage,
      pageSize,
    }: {
      active?: boolean;
      currentPage?: number;
      pageSize?: number;
    }) => {
      const parameterMap: { [key: string]: string } = {
        true: 'ativo=true',
        false: 'ativo=false',
      };

      const parameter =
        active !== undefined ? parameterMap[active.toString()] : undefined;

      const hasPagination = currentPage !== undefined && pageSize !== undefined;

      let url = parameter
        ? `api/Perfil/buscar?${parameter}`
        : 'api/Perfil/buscar';

      // ALTERAÇÃO: agora a paginação é adicionada sempre que existir,
      // e não apenas quando active === false.
      if (hasPagination) {
        const paginationQuery = `Page=${currentPage}&PageSize=${pageSize}`;

        url += parameter ? `&${paginationQuery}` : `?${paginationQuery}`;
      }

      fetchData(
        url,
        (data: {
          perfis: IProfileGroup[];
          pagination?: IPaginationGroupProfiles;
        }) => {
          setGroupState(prevState => ({
            ...prevState,
            profileGroups: Array.isArray(data?.perfis) ? data.perfis : [],
            profilePagination: data?.pagination ?? {},
          }));
        },
      );
    },
    [fetchData],
  );

  const fetchPermissionProfiles = useCallback(() => {
    fetchData(
      'api/Permissao/buscar',
      (data: { permissoes?: IPermissionProfiles[] }) => {
        setGroupState(prevState => ({
          ...prevState,
          permissionsProfile: Array.isArray(data?.permissoes)
            ? data.permissoes
            : [],
        }));
      },
    );
  }, [fetchData]);

  const insertOrEditPermissions = useCallback(
    async (updatedData: IProfileGroup | any, update?: boolean) => {
      const endpoint = `api/Perfil/${update ? 'editar' : 'cadastrar'}`;
      postData(endpoint, updatedData)
        .then((response: any) => {
          sendNotification(
            `Perfil ${response?.perfil?.nome || ''} ${
              update ? 'atualizados' : 'cadastrado'
            } com sucesso.`,
            toastConfig,
          );
        })
        .catch(err => {
          toast.error(err?.response?.data?.errors[0]);
        })
        .finally(() => {
          fetchProfilesGroups({ currentPage: 1, pageSize: 20 });
        });
    },
    [postData],
  );

  const updateUserCode = useCallback(
    async (email: string) => {
      setGroupState(prevState => ({
        ...prevState,
        isLoading: true,
        error: null,
      }));
      try {
        const endpoint = `api/atualizar-codigo`;
        const response = await postData(endpoint, { email });
        setGroupState(prevState => ({
          ...prevState,
          isLoading: false,
          error: null,
        }));
        return response;
      } catch (error: any) {
        toast.error(error.response.data.errors.at(0));
        setGroupState(prevState => ({
          ...prevState,
          error: error as any,
          isLoading: false,
        }));
      }
    },
    [postData, setGroupState],
  );

  const toggleActivatePermission = useCallback(
    async (id: number | any, active: boolean) => {
      const endpoint = `api/Perfil/trocastatus`;

      try {
        setGroupState(prevState => ({ ...prevState }));
        const response = await dealerProvider.put(endpoint, {
          id: id,
          ativo: active,
        });

        setGroupState(prevState => ({ ...prevState, error: null }));
        sendNotification(
          `Perfil ${active ? 'ativo' : 'desativado'} com sucesso.`,
          toastConfig,
        );
      } catch (err: any) {
        setGroupState(prevState => ({
          ...prevState,
          error: err as any,
        }));
      } finally {
        setGroupState(prevState => ({ ...prevState }));
      }
    },
    [],
  );

  const removeProfile = useCallback(async (id: number | string) => {
    const endpoint = `api/Perfil/remover`;

    try {
      setGroupState(prevState => ({ ...prevState }));
      const response = await dealerProvider.delete(endpoint, { data: { id } });
      setGroupState(prevState => ({ ...prevState, error: null }));
    } catch (err: any) {
      setGroupState(prevState => ({
        ...prevState,
        error: err as any,
      }));
      throw err;
    } finally {
      setGroupState(prevState => ({ ...prevState }));
    }
  }, []);

  return [
    groupState,
    // fetchEconomicGroups,
    fetchProfilesGroups,
    fetchPermissionProfiles,
    insertOrEditPermissions,
    toggleActivatePermission,
    removeProfile,
    fetchResponsibleChannel,
    updateUserCode,
  ];
};
